import { Location as NgLocation } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { filter, startWith, Subject } from 'rxjs';

import { ROUTES } from '../../../../definitions/constants/routes.constants';
import { Country } from '../../../../definitions/interfaces/country/country';
import { BaseCustomerConfig } from '../../../../definitions/interfaces/customer/customer-config';
import { UserConfigsStoreFacade } from '../../../../stores/global-store/user-configs/user-configs-store.facade';
import { SidebarHeartbeatComponent } from '../../../components/sidebar-heartbeat/sidebar-heartbeat.component';
import {
  CountryCustomerSelectionComponent
} from '../../../switch-view-sidebar/components/country-customer-selection/country-customer-selection.component';
import { SwitchExternalUserStore } from '../../state/switch-external-user-store/switch-external-user-store.service';

@Component({
  selector: 'app-switch-external-user-sidebar-content',
  imports: [
    CountryCustomerSelectionComponent,
    SidebarHeartbeatComponent
  ],
  templateUrl: './switch-external-user-sidebar-content.component.html',
  styleUrl: './switch-external-user-sidebar-content.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class SwitchExternalUserSidebarContentComponent implements OnInit {
  public primaryActionClicked = input.required<Subject<void>>();

  protected readonly userConfigsFacade = inject(UserConfigsStoreFacade);
  protected switchExternalUserStore = inject(SwitchExternalUserStore);
  private readonly ngLocation = inject(NgLocation);
  private destroyRef = inject(DestroyRef);

  private readonly window = window;

  public customerControl = new FormControl<string | BaseCustomerConfig | undefined>(
    undefined,
    { validators: [ Validators.required, this.customerValueValidator() ], nonNullable: true }
  );

  public countryControl = new FormControl<Country | undefined>(
    this.userConfigsFacade.currentCountrySignal(),
    { validators: [ Validators.required ]}
  );

  public ngOnInit(): void {
    if (this.primaryActionClicked()) {
      this.primaryActionClicked()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.switchToCustomer());
    }

    this.countryControl.valueChanges.pipe(
      startWith(this.countryControl.value),
      takeUntilDestroyed(this.destroyRef),
      filter((value): value is Country => !!value)
    ).subscribe((country: Country)=> {
      this.switchExternalUserStore.loadCustomersByCountryCode(country.code);
    });
  }

  private customerValueValidator(): ValidatorFn {
    return (control: AbstractControl) : ValidationErrors | null => {
      const value = control.value;

      return (typeof value === 'object') ? null : { invalidCustomerValue: true };
    };
  }

  private switchToCustomer(): void {
    const country = this.countryControl.value;
    const customer = this.customerControl.value;

    if (!country || !customer || typeof customer === 'string') return;

    const queryString = `view=EXTERNAL&countryCode=${country.code}&customerErpRef=${customer.erpRef}`;

    this.ngLocation.go(ROUTES.REPLENISHMENT, queryString);
    this.window.location.reload();
  }
}
